import React, {
  cloneElement, isValidElement, useCallback, useEffect, useId, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { EVIDENCE_BY_ID } from '../lib/constants';
import { termDef } from '../lib/terminology';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useStore } from '../state/store';
import { Sheet } from './Sheet';

function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="12" cy="8" r="1.15" fill="currentColor" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        d="M12 11.25v5.5"
      />
    </svg>
  );
}

/** Compact info-icon control that reveals a term explanation on hover, focus, or click. */
export function TermHelp({ term, label }) {
  const { state } = useStore();
  const def = termDef(term);
  const tipId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 280 });
  const closeTimer = useRef(null);
  const openBeforePointer = useRef(false);

  const clearClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearClose();
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  }, [clearClose]);

  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el || typeof window === 'undefined') return;
    const rect = el.getBoundingClientRect();
    const width = Math.min(280, Math.max(200, window.innerWidth - 24));
    // Prefer aligning under the icon; clamp into the viewport.
    let left = rect.left + rect.width / 2 - width / 2;
    if (left + width > window.innerWidth - 12) left = window.innerWidth - width - 12;
    if (left < 12) left = 12;
    const estimatedHeight = 130;
    const below = rect.bottom + 8;
    const top =
      below + estimatedHeight > window.innerHeight - 8
        ? Math.max(8, rect.top - estimatedHeight - 8)
        : below;
    setCoords({ top, left, width });
  }, []);

  useEffect(() => () => clearClose(), [clearClose]);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const tip = document.getElementById(tipId);
        if (tip && tip.contains(e.target)) return;
        setOpen(false);
      }
    };
    const onReposition = () => place();
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, place, tipId]);

  // Existing snapshots omit this setting; default to showing onboarding help.
  if (!def || state?.settings?.showTermHelp === false) return null;

  const title = def.title || label || 'About this term';

  const tip = open && typeof document !== 'undefined'
    ? createPortal(
      <span
        id={tipId}
        role="tooltip"
        className="term-help-tip"
        style={{ top: coords.top, left: coords.left, width: coords.width }}
        onMouseEnter={clearClose}
        onMouseLeave={scheduleClose}
      >
        <strong className="term-help-title">{title}</strong>
        <span className="term-help-body">{def.body}</span>
      </span>,
      document.body,
    )
    : null;

  return (
    <span
      className={`term-help ${open ? 'open' : ''}`}
      ref={wrapRef}
      onMouseEnter={() => { clearClose(); place(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="term-help-btn"
        aria-label={`Explain: ${title}`}
        aria-expanded={open}
        aria-controls={tipId}
        onPointerDown={() => { openBeforePointer.current = open; }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          clearClose();
          place();
          // Focus opens first in the same gesture; toggle from pre-pointer state.
          setOpen(!openBeforePointer.current);
        }}
        onFocus={() => { clearClose(); place(); setOpen(true); }}
        onBlur={scheduleClose}
      >
        <InfoIcon />
      </button>
      {tip}
    </span>
  );
}

function LabelWithTerm({ label, term, htmlFor }) {
  if (!label) return null;
  return (
    <label htmlFor={htmlFor} className="field-label">
      <span>{label}</span>
      {term ? <TermHelp term={term} label={typeof label === 'string' ? label : undefined} /> : null}
    </label>
  );
}

export function Card({ title, sub, right, children, className = '', flush = false, foot, term }) {
  return (
    <section className={`card ${flush ? 'flush' : ''} ${className}`}>
      {(title || right) && (
        <header className="card-head">
          {title && (
            <h3 className="card-title-row">
              <span>{title}</span>
              {term ? <TermHelp term={term} label={typeof title === 'string' ? title : undefined} /> : null}
            </h3>
          )}
          {sub && <span className="sub">{sub}</span>}
          <div className="spacer" />
          {right}
        </header>
      )}
      <div className="card-body">{children}</div>
      {foot}
    </section>
  );
}

export function Stat({ label, value, foot, size = '', tone, term }) {
  return (
    <div className="stat">
      <span className="label">
        <span>{label}</span>
        {term ? <TermHelp term={term} label={typeof label === 'string' ? label : undefined} /> : null}
      </span>
      <span className={`value ${size}`} style={tone ? { color: `var(--${tone})` } : undefined}>
        {value}
      </span>
      {foot && <span className="foot">{foot}</span>}
    </div>
  );
}

export function Chip({ tone = '', children, lg = false }) {
  return <span className={`chip ${tone} ${lg ? 'lg' : ''}`}>{children}</span>;
}

export function EvidenceBadge({ level, showName = false }) {
  const def = EVIDENCE_BY_ID[level] || EVIDENCE_BY_ID.E0;
  return (
    <span className={`ev ev-${def.id}`} title={def.line}>
      {def.id === 'ACT' ? 'ACTIVITY' : def.id}
      {showName ? ` · ${def.name}` : ''}
    </span>
  );
}

export function GateLight({ state = 'red', label }) {
  return (
    <span className="gate">
      <span className={`dot ${state}`} />
      <span>{label}</span>
    </span>
  );
}

export function Field({ label, help, children, style, term }) {
  const id = useId();
  return (
    <div className="field" style={style}>
      <LabelWithTerm label={label} term={term} htmlFor={id} />
      {isValidElement(children) ? cloneElement(children, { id }) : children}
      {help && <span className="help">{help}</span>}
    </div>
  );
}

export function Text({ label, value, onChange, placeholder, help, type = 'text', term }) {
  return (
    <Field label={label} help={help} term={term}>
      <input
        type={type}
        className={type === 'number' ? 'num-input' : undefined}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)}
      />
    </Field>
  );
}

export function Money({ label, value, onChange, currency, help, term }) {
  return (
    <Field label={currency ? `${label} (${currency})` : label} help={help} term={term}>
      <input
        type="number"
        className="num-input"
        value={value === '' || value === null || value === undefined ? '' : value}
        step="any"
        placeholder="Unknown"
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      />
    </Field>
  );
}

export function Area({ label, value, onChange, placeholder, help, rows, term }) {
  return (
    <Field label={label} help={help} term={term}>
      <textarea rows={rows} value={value ?? ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

export function Select({ label, value, onChange, options, help, term }) {
  return (
    <Field label={label} help={help} term={term}>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const val = typeof o === 'string' ? o : o.value;
          const lbl = typeof o === 'string' ? o : o.label;
          return (
            <option key={val} value={val}>
              {lbl}
            </option>
          );
        })}
      </select>
    </Field>
  );
}

export function Check({ label, checked, onChange, help }) {
  return (
    <label className="check">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {help && <span className="help" style={{ display: 'block' }}>{help}</span>}
      </span>
    </label>
  );
}

export function Segmented({ value, onChange, options, toneMap }) {
  return (
    <div className="seg">
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const lbl = typeof o === 'string' ? o : o.label;
        const tone = toneMap ? toneMap[val] || '' : '';
        return (
          <button key={val} type="button" className={value === val ? `on ${tone}` : ''} onClick={() => onChange(val)}>
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

const modalEscapeStack = [];

export function Modal({ title, sub, children, onClose, footer, wide = false }) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return undefined;
    modalEscapeStack.push(onClose);
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (modalEscapeStack[modalEscapeStack.length - 1] !== onClose) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      const idx = modalEscapeStack.lastIndexOf(onClose);
      if (idx >= 0) modalEscapeStack.splice(idx, 1);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose, isMobile]);

  if (isMobile) {
    return (
      <Sheet title={title} sub={sub} onClose={onClose} footer={footer} tall={wide}>
        {children}
      </Sheet>
    );
  }

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`modal ${wide ? 'wide' : ''}`}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="modal-head">
          <h3>{title}</h3>
          {sub && <span className="sub dim tiny">{sub}</span>}
          <div className="spacer" />
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">
            Close
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export function Empty({ children, title }) {
  return (
    <div className="empty">
      {title && <strong>{title}</strong>}
      {children}
    </div>
  );
}

export function Banner({ tone = '', children }) {
  return <div className={`banner ${tone}`}>{children}</div>;
}

export function Pipeline({ stages, value, onChange, canAdvance = true, blockedReason }) {
  const idx = stages.indexOf(value);
  return (
    <div className="pipeline">
      {stages.map((s, i) => {
        const cls = i === idx ? 'now' : i < idx ? 'done' : '';
        const forward = i > idx;
        const blocked = forward && !canAdvance;
        return (
          <button
            key={s}
            type="button"
            className={`step ${cls}`}
            disabled={blocked}
            title={blocked ? blockedReason : `Move to ${s}`}
            onClick={() => onChange && onChange(s)}
          >
            {i + 1}. {s}
          </button>
        );
      })}
    </div>
  );
}

export function BarRow({ label, actual, target, suffix = '%' }) {
  const width = Math.min(100, Math.max(0, actual));
  const over = target !== undefined && actual > target + 5;
  const under = target !== undefined && actual < target - 5;
  return (
    <div className="bar-row">
      <span className="lbl">{label}</span>
      <span className="track">
        <span className={`fill ${over ? 'over' : under ? 'under' : ''}`} style={{ width: `${width}%` }} />
        {target !== undefined && <span className="target" style={{ left: `${Math.min(100, target)}%` }} />}
      </span>
      <span className="val">
        {actual}
        {suffix}
        {target !== undefined && <span className="dim"> / {target}{suffix}</span>}
      </span>
    </div>
  );
}

export function KV({ items }) {
  return (
    <dl className="kv">
      {items.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd>{v === '' || v === null || v === undefined ? <span className="dim">—</span> : v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export function ChecklistRow({ ok, label, detail }) {
  return (
    <div className="row tight" style={{ padding: '5px 0', alignItems: 'flex-start' }}>
      <span className={`dot ${ok ? 'green' : 'red'}`} style={{ marginTop: 5 }} />
      <span style={{ flex: 1 }}>
        <span style={{ fontSize: 13.5 }}>{label}</span>
        {detail && <span className="dim tiny" style={{ display: 'block' }}>{detail}</span>}
      </span>
    </div>
  );
}

export function SectionHead({ title, hint, right, term }) {
  return (
    <div className="section-head">
      <h3 className="card-title-row">
        <span>{title}</span>
        {term ? <TermHelp term={term} label={typeof title === 'string' ? title : undefined} /> : null}
      </h3>
      {hint && <span className="hint">{hint}</span>}
      <div className="spacer" />
      {right}
    </div>
  );
}
